import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Eye, TrendingUp, Heart, Award } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const BADGE_ICONS = {
  trending_now: { icon: Flame, color: '#EF4444', label: 'Trending' },
  most_read_today: { icon: Eye, color: '#F59E0B', label: 'Most Read' },
  highly_engaged: { icon: Heart, color: '#EC4899', label: 'Highly Engaged' },
  fast_rising: { icon: TrendingUp, color: '#10B981', label: 'Fast Rising' },
};

export default function TrendingSection() {
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const res = await axios.get(`${API}/trending-books?limit=10`);
        setTrending(res.data);
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);

  if (loading) {
    return (
      <section data-testid="trending-section" id="trending" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-2">
            <Flame className="w-6 h-6 text-red-500" /> TRENDING NOW
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={`trending-skeleton-${i}`} className="bg-zinc-900 rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!trending.length) return null;

  return (
    <section data-testid="trending-section" className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Flame className="w-6 h-6 text-red-500" /> TRENDING NOW
          </h2>
          <span className="text-xs text-zinc-500 uppercase tracking-wider" data-testid="trending-quarter-label">
            {`Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`} · resets quarterly
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {trending.map((book, idx) => (
            <div
              key={book.book_id}
              data-testid={`trending-book-${idx}`}
              onClick={() => navigate(`/book/${book.book_id}`)}
              className="group relative bg-zinc-900/80 rounded-xl overflow-hidden border border-zinc-800 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            >
              {/* Rank Badge */}
              {idx < 3 && (
                <div className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-red-600 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                  {idx + 1}
                </div>
              )}

              {/* Trending Badges */}
              {book.badges?.length > 0 && (
                <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
                  {book.badges.map(badge => {
                    const badgeDef = BADGE_ICONS[badge];
                    if (!badgeDef) return null;
                    const Icon = badgeDef.icon;
                    return (
                      <div
                        key={badge}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ backgroundColor: `${badgeDef.color}20`, color: badgeDef.color }}
                        title={badgeDef.label}
                      >
                        <Icon className="w-3 h-3" />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Cover */}
              <div className="aspect-[3/4] bg-zinc-800 overflow-hidden">
                {book.cover_image_url ? (
                  <img
                    src={book.cover_image_url.startsWith('http') ? book.cover_image_url : `${window.location.origin}${book.cover_image_url}`}
                    alt={book.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600">
                    <Award className="w-10 h-10" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <h3 className="text-sm font-semibold text-white truncate">{book.title}</h3>
                <p className="text-xs text-zinc-400 truncate mt-0.5">{book.author}</p>

                {/* Rating & Reviews */}
                <div className="flex items-center gap-1.5 mt-1.5 text-xs">
                  <span className="text-yellow-400 font-semibold">★ {book.rating || 0}</span>
                  {book.total_reviews ? (
                    <span className="text-zinc-500">({book.total_reviews})</span>
                  ) : null}
                </div>

                <div className="flex items-center justify-between mt-1.5 text-[10px] text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {book.total_views}
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {book.completion_rate}%
                  </span>
                </div>

                {/* Trending Score Bar */}
                <div className="mt-2">
                  <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(book.trending_score, 100)}%`,
                        background: `linear-gradient(90deg, #EF4444, #F59E0B)`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
